import './styles/ReviewInstructions.scss';
import React, { useEffect, useState } from 'react';
import history from 'shared_history';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, withRouter } from 'react-router';
import { compose } from 'redux';
import { currentUser, duckify } from 'utils/duckify';
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';
import { ReviewContextType, UserType } from '../../../propTypes';
import PropTypes from 'prop-types';

/**
 * Dismissible reader instructions.
 *
 * The dismissed state is recorded in browser storage and is scoped to a year, meaning if the notice is dismissed
 * for the current year it will still display for the next year.
 *
 * @param currentUser the current user object
 * @param reviewContext the review context object
 * @param schoolYearId the current review year
 * @param location the browser location object provided by withRouter
 * @return {JSX.Element|null} if dismissed, returns null
 */
const ReviewInstructions = ({ currentUser, reviewContext, schoolYearId, location }) => {
  const { paginator } = reviewContext;
  const { nextPage } = paginator;
  const [dismissed, setDismissed] = useState(true);
  const { reset, ...search } = queryString.parse(location.search);
  const storageKey = `review_notice:${schoolYearId}:dismissed`;

  const dismiss = (storage) => {
    storage.setItem(storageKey, 'true');
    setDismissed(true);
  };

  const dismissTemporary = () => dismiss(window.sessionStorage);
  const dismissPermanent = () => dismiss(window.localStorage);

  useEffect(() => {
    if (schoolYearId) {
      setDismissed(
        window.sessionStorage.getItem(storageKey) === 'true' ||
        window.localStorage.getItem(storageKey) === 'true'
      );
    }
  }, [currentUser, schoolYearId]);

  // for testing, add "?reset" to undo dismissal
  useEffect(() => {
    if (reset !== undefined && schoolYearId) {
      window.sessionStorage.removeItem(storageKey);
      window.localStorage.removeItem(storageKey);
      history.push({...location, search: queryString.stringify(search)});
      setDismissed(false);
    }
  }, [reset, schoolYearId]);

  if (dismissed) {
    return null;
  }

  return (
    <div className="review-cover-sheet-notice">
      <button className="btn-close" aria-label="Close" onClick={() => dismissTemporary()}/>

      <div className="review-cover-sheet-notice-header">
        {currentUser.first_name}, thank you for taking the time to review this candidate.
      </div>

      <ul className="review-cover-sheet-notice-instructions">
        <li>
          <FontAwesomeIcon icon={faList}/>
          {' '}This is your Cover Sheet for this candidate, where your comments and scores will be saved when you
          finish reviewing.
        </li>
        <li>
          <FontAwesomeIcon icon={faBook}/>
          {' '}Choose "Start Reading" or use the document menu at the bottom of your screen to read through the
          application documents.
        </li>
        <li>
          <FontAwesomeIcon icon={faEdit}/>
          {' '}Add comments for each document as you go. You’ll be prompted to enter your score(s) for this candidate
          when you finish reading.
        </li>
      </ul>

      <div><Link className="btn btn-success" to={nextPage && nextPage.href}>Start Reading</Link></div>
      <div>
        <a className="review-cover-sheet-notice-dismiss-link" onClick={() => dismissPermanent()}>
          Got it! Don't show this again.
        </a>
      </div>
    </div>
  );
};

ReviewInstructions.propTypes = {
  currentUser: UserType.isRequired,
  reviewContext: ReviewContextType.isRequired,
  schoolYearId: PropTypes.string,
  location: PropTypes.object.isRequired,
};

const mapDucksToProps = () => ({ currentUser });

export default compose(
  withRouter,
  duckify(mapDucksToProps)
)(ReviewInstructions);
